import { DataColumnMapping } from './types'

export function parseCrosswalkData(crosswalkData: string[][]) {
  for (const parser of [
    parseOldStyleParser,
    parseNewStyleParser,
    unknownParser,
  ]) {
    const result = parser(crosswalkData)
    if (result) return result
  }
}

function parseOldStyleParser(crosswalkData: string[][]) {
  const dataRowRegex = /^Grade\s+[A-Z0-9]+$/i
  const getRowMatcher =
    (negate: boolean = false) =>
    (rowData: string[]) => {
      return negate !== dataRowRegex.test(rowData[0])
    }

  const dataStart = crosswalkData.findIndex(getRowMatcher())

  if (dataStart < 0) return

  const headers = crosswalkData[dataStart - 1]

  const sourceMatcher = (s: string) =>
    s.toLowerCase().includes('main criteria standards')
  const targetMatcher = (s: string) =>
    s.toLowerCase().includes('enter your alignment')

  const mapping = {
    sourceStandardCode: headers.findIndex(sourceMatcher),
    targetStandardCode: headers.findIndex(targetMatcher),
    rating: headers.indexOf('Rating'),
    note: headers.indexOf('Notes'),
  }

  if (Object.values(mapping).some((v) => v < 0)) return

  const rawData = crosswalkData.slice(dataStart).filter(getRowMatcher(true))

  return {
    frameworks: {
      source_framework_id: getLabelValueFromString(crosswalkData[0][0]),
      target_framework_id: getLabelValueFromString(crosswalkData[1][0]),
    },
    subject: getLabelValueFromString(crosswalkData[2][0]),
    rawData: {
      headers,
      data: rawData,
      mapping,
    },
    parsed: parseMappingContent(rawData, mapping),
  }
}

function parseNewStyleParser(crosswalkData: string[][]) {
  // Try to find headers by looking for "State Standards" and "Map to CCSS"
  const headerIndex = crosswalkData.findIndex(
    (row) =>
      row.some((cell) => cell === 'State Standards') &&
      row.some((cell) => cell === 'Map to CCSS'),
  )

  if (headerIndex < 0) return

  const headers = crosswalkData[headerIndex]

  const mapping = {
    sourceStandardCode: headers.findIndex((s) => s === 'State Standards'),
    targetStandardCode: headers.findIndex((s) => s === 'Map to CCSS'),
    rating: headers.indexOf('Rating'),
    note: headers.indexOf('Notes'),
  }

  if (Object.values(mapping).some((v) => v < 0)) return

  const rawData = crosswalkData.slice(headerIndex + 1)

  // Try to extract metadata if it exists before headers
  // Check if there's content before the header row that looks like metadata
  const hasMetadata = headerIndex >= 2
  const subject = hasMetadata ? (crosswalkData[0]?.[0] ?? '') : ''
  const sourceFrameworkId = hasMetadata ? (crosswalkData[1]?.[0] ?? '') : ''

  return {
    frameworks: {
      source_framework_id: sourceFrameworkId,
      target_framework_id: 'CCSS',
    },
    subject: subject,
    rawData: {
      headers,
      data: rawData,
      mapping,
    },
    parsed: parseMappingContent(rawData, mapping),
  }
}

function getLabelValueFromString(s?: string) {
  return s?.split(':').pop()?.trim()
}

/**
 * Matches standard codes in three formats:
 * 1. State-prefixed: CA-5.NBT.A.1, NY-5.NBT.A.1
 * 2. Letter-starting: K.CC.A.1, CCSS.Math.Content.5.NBT.A.1
 * 3. Digit-starting (e.g. Georgia): 2.MP8, 5.NBT.A.1, 8.EE.B.5
 */
const STANDARD_CODE_RE =
  /\b(?:[A-Z]{2}-[A-Z0-9]+(?:\.[A-Z0-9]+)*(?:\.[a-z]{1,3})?|[A-Z]{1,4}\.[A-Za-z0-9]+(?:\.[A-Za-z0-9]+)*(?:\.[a-z]{1,3})?|[0-9K]\.[A-Z]+[A-Z0-9]*(?:\.[A-Z0-9]+)*(?:\.[a-z]{1,3})?)\.?/g

export function parseMappingContent(
  data: string[][],
  columnMapping: DataColumnMapping,
) {
  return data.map((row, srcIdx) => {
    const { sourceStandardCode, targetStandardCode, rating, note } =
      columnMapping

    return {
      sourceStandardCode:
        row[sourceStandardCode].match(STANDARD_CODE_RE)?.[0] ?? '',
      targetStandardCode: row[targetStandardCode].split(' - ')[0].trim(),
      rating: row[rating],
      note: row[note],
      srcIdx,
    }
  })
}

function unknownParser(crosswalkData: string[][]) {
  const maxLength = Math.max(...crosswalkData.map((a) => a.length))
  return {
    frameworks: {
      source_framework_id: getLabelValueFromString(crosswalkData[0][0]),
      target_framework_id: getLabelValueFromString(crosswalkData[1][0]),
    },
    subject: getLabelValueFromString(crosswalkData[2][0]),
    rawData: {
      headers: Array.from({ length: maxLength }, () => ''),
      data: crosswalkData,
      mapping: {},
    },
    parsed: [],
  }
}
